export const REGION_OPTIONS = ['APJ', 'NA', 'EMEA', 'LATAM'];
export const PARTNER_TYPE = 'Partner';
export const OTHERS_TYPE = 'Others';
export const EXTERNAL_SE_TYPE = 'External SE';
export const BUSINESS_UNIT_OPTIONS = ['DSOM', 'Other'];

export const ADMIN_CREATE_ACCOUNT_TYPE_OPTIONS = [
  { label: 'Internal', value: 'Internal SE' },
  { label: 'External', value: 'External SE' },
  { label: 'Technical', value: 'Technical User' },
  { label: 'Sales', value: 'Sales' },
  { label: 'Leadership', value: 'Leadership' },
];

export const PENDING_ROLE_OPTIONS = [
  'R&D',
  'SQA',
  'PM',
  'Channel Partner',
  'DSOM',
  'GOSI',
  'DCOE',
  'FEDERAL',
  'Services',
  'CSM',
  'DDE',
  'Sales',
  'Admin',
  'Others',
];

export const FALLBACK_TYPE_OF_USER_OPTIONS = [
  'Internal SE',
  'External SE',
  'Technical User',
  'Sales',
  OTHERS_TYPE,
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const getDefaultCreateAccountFormState = () => ({
  firstName: '',
  lastName: '',
  email: '',
  typeOfUser: '',
  company: '',
  businessUnit: 'DSOM',
  customBusinessUnit: '',
  otherTypeOfUser: '',
  country: '',
  managerName: '',
  region: '',
  isManager: 'No',
  role: '',
  customRole: '',
});

export const normalizeRoleValue = (value) => (value === 'DSOM SE' ? 'DSOM' : value || '');

export const resolveRoleValue = (formData) => {
  if (formData.role === 'Others') {
    return (formData.customRole || '').trim();
  }

  return normalizeRoleValue(formData.role);
};

const requiresManualCompanyEntry = (typeOfUser) =>
  typeOfUser === PARTNER_TYPE
  || typeOfUser === OTHERS_TYPE
  || typeOfUser === EXTERNAL_SE_TYPE;

export const deriveCreateAccountValues = (formData) => {
  const isPartner = formData.typeOfUser === PARTNER_TYPE;
  const isOthers = formData.typeOfUser === OTHERS_TYPE;
  const isExternalSe = formData.typeOfUser === EXTERNAL_SE_TYPE;
  const requiresCompanyInput = requiresManualCompanyEntry(formData.typeOfUser);
  const isBmcCompanyType = Boolean(formData.typeOfUser) && !requiresCompanyInput;

  const finalCompanyValue = isBmcCompanyType ? 'BMC' : formData.company.trim();
  const finalTypeOfUserValue = isOthers
    ? formData.otherTypeOfUser.trim()
    : formData.typeOfUser;
  const finalBusinessUnitValue =
    formData.businessUnit === 'Other'
      ? formData.customBusinessUnit.trim()
      : formData.businessUnit;

  return {
    isPartner,
    isOthers,
    isExternalSe,
    requiresCompanyInput,
    isBmcCompanyType,
    finalCompanyValue,
    finalTypeOfUserValue,
    finalBusinessUnitValue,
  };
};

export const applyCreateAccountFieldChange = (prev, name, value) => {
  const updated = {
    ...prev,
    [name]: value,
  };

  if (name === 'typeOfUser') {
    updated.businessUnit = 'DSOM';
    updated.customBusinessUnit = '';
    updated.otherTypeOfUser = '';

    if (requiresManualCompanyEntry(value)) {
      updated.company = '';
    } else if (value) {
      updated.company = 'BMC';
    } else {
      updated.company = '';
    }
  }

  if (name === 'businessUnit' && value !== 'Other') {
    updated.customBusinessUnit = '';
  }

  if (name === 'role' && value !== 'Others') {
    updated.customRole = '';
  }

  return updated;
};

export const buildAccountTypeOptions = (typeOfUserOptions = []) => {
  const normalizedTypeOfUserOptions = Array.from(
    new Set(
      typeOfUserOptions
        .filter((option) => typeof option === 'string')
        .map((option) => option.trim())
        .filter(Boolean)
    )
  );

  if (normalizedTypeOfUserOptions.length === 0) {
    return [...FALLBACK_TYPE_OF_USER_OPTIONS];
  }

  return normalizedTypeOfUserOptions.includes(OTHERS_TYPE)
    ? normalizedTypeOfUserOptions
    : [...normalizedTypeOfUserOptions, OTHERS_TYPE];
};

export const validateCreateAccountForm = (formData) => {
  const errors = {};
  const {
    finalCompanyValue,
    finalTypeOfUserValue,
    finalBusinessUnitValue,
  } = deriveCreateAccountValues(formData);

  if (!formData.firstName.trim()) {
    errors.firstName = 'First Name is required';
  }

  if (!formData.lastName.trim()) {
    errors.lastName = 'Last Name is required';
  }

  if (!formData.email.trim()) {
    errors.email = 'Email is required';
  } else if (!EMAIL_PATTERN.test(formData.email.trim())) {
    errors.email = 'Enter a valid email address';
  }

  if (!finalTypeOfUserValue) {
    errors.typeOfUser = 'Type of User is required';
  }

  if (!finalBusinessUnitValue) {
    errors.businessUnit = 'Business Unit is required';
  }

  if (!finalCompanyValue) {
    errors.company = 'Company is required';
  }

  if (!formData.country) {
    errors.country = 'Country is required';
  }

  if (!formData.region) {
    errors.region = 'Region is required';
  }

  if (!formData.managerName.trim()) {
    errors.managerName = 'Manager Name is required';
  }

  if (formData.role === 'Others' && !(formData.customRole || '').trim()) {
    errors.customRole = 'Custom role is required when Role is Others';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const buildAdminCreateAccountPayload = (formData) => {
  const {
    finalCompanyValue,
    finalTypeOfUserValue,
    finalBusinessUnitValue,
  } = deriveCreateAccountValues(formData);

  return {
    email: formData.email.trim(),
    firstname: formData.firstName.trim(),
    lastname: formData.lastName.trim(),
    region: formData.region,
    subregion: '',
    company: finalCompanyValue,
    typeOfUser: finalTypeOfUserValue,
    roleOfUser: resolveRoleValue(formData) || '',
    isManager: formData.isManager === 'Yes',
    canDemo: true,
    manager: formData.managerName.trim(),
    businessUnit: finalBusinessUnitValue,
    country: formData.country,
  };
};

export const mapCreateAdminAccountError = (message = '') => {
  const normalized = String(message).trim();

  if (normalized === 'Email already exist in the system.') {
    return {
      submitError: 'An account already exists for this email.',
      fieldErrors: { email: 'An account already exists for this email.' },
    };
  }

  if (normalized === 'Please enter a valid email string.') {
    return {
      submitError: 'Enter a valid email address.',
      fieldErrors: { email: 'Enter a valid email address.' },
    };
  }

  if (normalized === 'Missing fields.') {
    return {
      submitError: 'Please complete all required fields.',
      fieldErrors: {},
    };
  }

  if (/permission|forbidden|not authorized|not authorised/i.test(normalized)) {
    return {
      submitError: 'You do not have permission to create accounts.',
      fieldErrors: {},
    };
  }

  if (
    normalized === 'Something went wrong' ||
    normalized === 'Unable to create the account. Please try again.'
  ) {
    return {
      submitError: 'Unable to create the account. Please try again.',
      fieldErrors: {},
    };
  }

  if (normalized === 'Server is currently down. Please try again later.') {
    return {
      submitError: normalized,
      fieldErrors: {},
    };
  }

  return {
    submitError: normalized || 'Unable to create the account. Please try again.',
    fieldErrors: {},
  };
};

export const buildCredentialsCopyText = (username, password) =>
  `Tech Central account credentials\n\nUsername: ${username}\nPassword: ${password}`;

export const buildResetPasswordCredentialsCopyText = (username, password) =>
  `Tech Central login credentials\n\nUsername: ${username}\nPassword: ${password}`;

export const mapResetAdminUserPasswordError = (message = '') => {
  const normalized = String(message).trim();

  if (normalized === 'User not found.') {
    return 'This user could not be found.';
  }

  if (
    normalized === 'Missing fields.' ||
    normalized === 'Email is required.' ||
    /missing.*email|email.*required/i.test(normalized)
  ) {
    return 'This reset request is missing required information.';
  }

  if (/permission|forbidden|not authorized|not authorised/i.test(normalized)) {
    return 'You do not have permission to reset this user\'s password.';
  }

  if (
    normalized === 'Something went wrong' ||
    normalized === 'Unable to reset the password. Please try again.'
  ) {
    return 'Unable to reset the password. Please try again.';
  }

  if (
    normalized === 'Server is currently down. Please try again later.' ||
    normalized === 'Server is currently unavailable. Please try again later.'
  ) {
    return 'Server is currently unavailable. Please try again later.';
  }

  return normalized || 'Unable to reset the password. Please try again.';
};
